import React, { Dispatch, SetStateAction, useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Grid, Box, SvgIcon } from "@mui/material";
import {
    StriimDataTable,
    StriimTooltip,
    StriimControlLabel,
    StriimCircularLoader,
    StriimRadio,
    StriimTypography
} from "@striim/striim-ui";
import { AgGridReact } from "ag-grid-react";
// @ts-ignore
import BlockIcon from "app/images/Block.svg";
import { style } from "./select-schemas.styles";
// @ts-ignore
import { Schema } from "../select-tables-modal/schema-table";
import SearchBarContainer from "./search-bar-container";
import useDebounce from "src/hooks/useDebounce";
import InfoIcon from "app/images/striimline/info-icon.svg";
import WithTooltip from "src/generic/tooltip/tooltip";
import EllipsisText from "src/generic/ellipsis-text/ellipsis-text";
import SearchList from "app/images/SearchList.svg";
import { matchTextWithWildcard } from "../select-tables-modal/select-tables.utils";
import { RowNode, GetRowIdParams, ICellRendererParams } from "ag-grid-community";

interface SelectSchemasProps {
    schemas: Schema[];
    selectedSchemas: Schema[];
    setSelectedSchemas: Dispatch<SetStateAction<Schema[]>>;
    schemaType: string;
    showSystemSchemas?: boolean;
    isLoading?: boolean;
    maxHeight: string | null;
}

interface NoDataComponentProps {
    searchQuery: string;
    schemaType: string;
}

const NoDataComponent: React.FC<NoDataComponentProps> = ({ searchQuery, schemaType }) => {
    return (
        <Grid container justifyContent="center" alignItems="center" flexDirection="column" gap={2} py={8}>
            {!!searchQuery?.length ? (
                <>
                    <SvgIcon component={SearchList} sx={style.searchListIcon} />
                    <Grid
                        item
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        data-test-id="no-schemas-found-message"
                    >
                        <StriimTypography variant="h4" color="primary.900">
                            No {schemaType?.toLowerCase()}s matching "{searchQuery}"
                        </StriimTypography>
                        <StriimTypography variant="body4" color="greyscale.700">
                            Try checking for typos or search using another keyword
                        </StriimTypography>
                    </Grid>
                </>
            ) : (
                <>
                    <SvgIcon component={SearchList} sx={style.searchListIcon} />
                    <StriimTypography variant="body3" color="greyscale.700">
                        No {schemaType?.toLowerCase()}s found.
                    </StriimTypography>
                </>
            )}
        </Grid>
    );
};

const Header = params => {
    const { schemaType, selectedRows, totalRows } = params;
    return schemaType === "Channel" ? (
        <Box display="flex" alignItems="center" gap={0.5} ml={4}>
            <StriimTypography variant="body3" color="greyscale.600">
                Channel Name
            </StriimTypography>
            {WithTooltip(<SvgIcon component={InfoIcon} sx={style.infoIcon} />, <>Select a channel to proceed</>)}
        </Box>
    ) : (
        `Source ${schemaType ? `${schemaType.charAt(0).toLowerCase() + schemaType.slice(1)}s` : "schemas"} ${
            selectedRows ? `(${selectedRows}/${totalRows})` : `(${totalRows})`
        }`
    );
};

const SelectSchemas: React.FC<SelectSchemasProps> = ({
    schemas,
    selectedSchemas = [],
    setSelectedSchemas,
    schemaType,
    showSystemSchemas = false,
    isLoading = false,
    maxHeight
}) => {
    const gridRef = useRef<AgGridReact<Schema>>(null);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [singleSelectionId, setSingleSelectionId] = useState<number | null>(null);
    const [multiSelectionIds, setMultiSelectionIds] = useState<Set<number>>(new Set());
    const [filteredSchemas, setFilteredSchemas] = useState<Schema[]>(schemas);
    const selectedCountInFiltered = useMemo(() => {
        const filteredIds = new Set(filteredSchemas.map(schema => schema.id));
        return selectedSchemas.filter(schema => filteredIds.has(schema.id)).length;
    }, [filteredSchemas, selectedSchemas]);
    const getRowId = useCallback((params: GetRowIdParams) => `schema-${String(params.data.name)}`, []);
    const tableColumns = useMemo(
        () => [
            {
                headerComponent: Header,
                headerComponentParams: {
                    schemaType: schemaType,
                    selectedRows: selectedCountInFiltered,
                    totalRows: filteredSchemas?.length
                },
                field: "name",
                flex: 1,
                suppressMovable: true,
                resizable: true,
                cellRenderer: params => {
                    const isSelected = singleSelectionId === params.data.id;

                    return schemaType === "Channel" ? (
                        <StriimControlLabel
                            style={{ position: "absolute", left: "13px", top: "7px", gap: "10px" }}
                            xs={3}
                            value={params.data.name}
                            label={
                                <StriimTypography variant="body4" color="greyscale.900">
                                    {params.data.name}
                                </StriimTypography>
                            }
                            control={
                                <StriimRadio
                                    checked={isSelected}
                                    onChange={() => {
                                        gridRef.current?.api?.deselectAll();
                                        const rowNode = gridRef.current?.api?.getRowNode(params.node.id);
                                        rowNode?.setSelected(true);
                                        setSingleSelectionId(params.data.id);
                                    }}
                                />
                            }
                        />
                    ) : params.data.isDisabled ? (
                        <Box display="flex" alignItems="center" gap={3} left={"13px"} position={"absolute"}>
                            <StriimTooltip title={params.data.errors} placement="right">
                                <Box display="flex" alignItems="center" justifyContent="center">
                                    <BlockIcon style={{ fontSize: 20, fill: "none" }} />
                                </Box>
                            </StriimTooltip>
                            <EllipsisText text={params.data.name} sx={style.schemaName} />
                        </Box>
                    ) : (
                        <EllipsisText text={params.value} sx={style.schemaName} />
                    );
                },
                cellClass: "appNamecellClass",
                headerCheckboxSelection: schemaType !== "Channel",
                checkboxSelection: params => !params.data.isDisabled && schemaType !== "Channel"
            },
            {
                headerName: "Type",
                field: "type",
                flex: 1,
                suppressMovable: true,
                resizable: true,
                cellRenderer: ({ value }) => (
                    <StriimTypography variant="body4" color="greyscale.900">
                        {value}
                    </StriimTypography>
                ),
                hide: !showSystemSchemas
            }
        ],
        [selectedSchemas, filteredSchemas, singleSelectionId]
    );

    const initialState = useMemo(() => selectedSchemas.map(row => row.id), [selectedSchemas]);

    useEffect(() => {
        setFilteredSchemas(schemas);
    }, [schemas]);

    useEffect(() => {
        if (gridRef.current && gridRef.current.api && initialState.length > 0) {
            setTimeout(() => {
                gridRef.current.api.forEachNode(node => {
                    node.setSelected(multiSelectionIds.has(node.data.id));
                });
            }, 100);
        }
    }, [initialState, filteredSchemas]);

    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setSelectedSchemas(schemas.filter(schema => multiSelectionIds.has(schema.id)));
    }, [multiSelectionIds]);

    const onFirstDataRendered = useCallback(
        params => {
            if (initialState.length > 0) {
                setTimeout(() => {
                    params.api.forEachNode(node => {
                        if (initialState.includes(node.data.id)) {
                            node.setSelected(true);
                            setSingleSelectionId(node.data.id);
                        }
                    });
                }, 100);
            }
        },
        [initialState]
    );

    const onRowSelected = useCallback(event => {
        setMultiSelectionIds(prevSelected => {
            const updatedSelection = new Set(prevSelected);
            const rowId = event.data.id;
            if (event.node.isSelected()) {
                updatedSelection.add(rowId);
            } else {
                updatedSelection.delete(rowId);
            }
            return updatedSelection;
        });
    }, []);

    const matchSchemaName = (schemaName: string, searchText: string) => {
        return matchTextWithWildcard(schemaName, searchText, false);
    };

    const filterSchemas = () => {
        setFilteredSchemas(
            schemas.filter(
                schema => (!isChecked || schema.type === "SYSTEM") && matchSchemaName(schema.name, searchQuery)
            )
        );
    };
    const setFiltersDebounced = useDebounce(filterSchemas, 500);

    const onCellClicked = useCallback(
        (event: any) => {
            if (event.data.isDisabled || schemaType === "Channel") return;
            event.node.setSelected(!event.node.isSelected());
        },
        [schemaType]
    );

    const gridOptions = {
        getRowClass: params => (params.node.rowIndex === params.api.getDisplayedRowCount() - 1 ? "last-row" : "")
    };

    return (
        <Grid
            display="flex"
            direction="column"
            width="100%"
            height="100%"
            alignItems="center"
            justifyContent={isLoading ? "center" : "flex-start"}
        >
            {isLoading ? (
                <Grid item display={"flex"} alignItems={"center"} gap={1} justifySelf={"center"}>
                    <StriimCircularLoader />
                    <StriimTypography variant="body1">Loading {schemaType}s</StriimTypography>
                </Grid>
            ) : (
                <>
                    <SearchBarContainer
                        schemaType={schemaType}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        setFiltersDebounced={setFiltersDebounced}
                        showSystemSchemas={showSystemSchemas}
                        isChecked={isChecked}
                        setIsChecked={setIsChecked}
                    />
                    <Box width="100%" maxHeight={"91.5%"} data-test-id="select-schemas-table">
                        <StriimDataTable
                            ref={gridRef}
                            data={filteredSchemas}
                            columns={tableColumns}
                            rowSelection={schemaType === "Channel" ? "single" : "multiple"}
                            onRowSelected={onRowSelected}
                            onFirstDataRendered={onFirstDataRendered}
                            onCellClicked={onCellClicked}
                            gridOptions={gridOptions}
                            NoRowsComponent={<NoDataComponent schemaType={schemaType} searchQuery={searchQuery} />}
                            getRowId={getRowId}
                            {...(maxHeight != null ? { maxHeight } : {})}
                        />
                    </Box>
                </>
            )}
        </Grid>
    );
};

export default SelectSchemas;
